import React, { ReactNode } from "react";
import moment from "moment";
import { defineMessages, useIntl } from "react-intl";
import { IconLoader3 } from "@tabler/icons-react";
import { classNames } from "@ct-react/core";
import { useLocaleFormatter, useLocaleNavigate } from "@ct-react/locale";
import { globalTranslations } from "../../i18n/sharable-defs";
import { DataProps } from "../../tools/components";
import { CartItem as CartItemModel } from "../../models/cart";
import CartItemPrice, { CartItemCharge } from "./cart-price";
import { Asset, Ratio } from "../../models/medias";
import { Button } from "../common/minimals";
import RatioImage from "../common/ratio-image";
import InfoBox from "../common/info-box";
import { CartItemCountdown } from "../../code-splitting/others";
import "./cart-item.scss";

type CartItemProps = DataProps<CartItemModel> & {
  withPrice?: boolean;
  nextStep?: ReactNode;
  actionRemoving?: boolean;
  onRemove?: (id: string) => void;
}

const transDefs = defineMessages({
  dates: { id: "cart-item-book-label", defaultMessage: "Dates sélectionnées" },
  last: { id: "cart-last-minute-warning", defaultMessage: "La montant total devra être payé immédiatement." }
});

export const LastMinuteWarning = () => {
  const intl = useIntl();
  return (<InfoBox minimal={true} inline={true} msg={intl.formatMessage(transDefs.last)} />);
}

const CartItem = (
  {
    className,
    data,
    withPrice = true,
    nextStep,
    actionRemoving = false,
    onRemove
  }: CartItemProps) => {

  const intl = useIntl();
  const { print } = useLocaleFormatter();
  const navigate = useLocaleNavigate();

  const wrapperClasses = classNames("cart-item", className);
  const priceCollWrapperClasses = classNames("cart-item-price", { "with-action": !!nextStep });

  return (
    <article className={wrapperClasses}>
      <h1>{data.title}</h1>
      <span className="desc">{data.description}</span>
      <div className="cart-item-details">

        <div className="cart-item-resume">
          {!!data.image && <RatioImage images={[ { url: data.image } as Asset ]} ratio={Ratio.SIXTEEN_NINE} alt={data.title} />}
          <div className="period">
              <span className="label">
                {intl.formatMessage(transDefs.dates)}
              </span>
            <span className="value">{print.period(data.period, true)}</span>
          </div>
          {!!onRemove &&
            <div className="actions">
              <Button type="button"
                      className={classNames("secondary bolder loadable-keeping-size", { loading: actionRemoving })}
                      onClick={() => onRemove(data.itemId)}>
                <span className="text">{intl.formatMessage(globalTranslations.cancel)}</span>
                <div className="loader"><IconLoader3 /></div>
              </Button>
            </div>
          }
          {!!data.expirationDate &&
            <div className="cart-item-lifetime">
                <CartItemCountdown expirationDate={moment(data.expirationDate)}
                                   onRefresh={() => navigate(0)} />
            </div>
          }
        </div>

        <div className={priceCollWrapperClasses}>
          {withPrice &&
            <>
              <CartItemPrice data={data.price} />
              {!!data.downPayment.providerCharge && <CartItemCharge data={data.downPayment} />}
              {data.downPayment.rate === 1 && <LastMinuteWarning />}
            </>
          }
          {nextStep}
        </div>

      </div>
    </article>);

}

export default CartItem;
